"use client"

import {useEffect, useRef, useState} from "react";
import {Menu, MenuItem, useMediaQuery, useTheme} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ScrollableChips = ({categories, selectedNav, setSelectedNav}) => {
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [preferredCategories, setPreferredCategories] = useState([...(categories || []).slice(0, 4)]);
    const [otherCategories, setOtherCategories] = useState((categories || []).slice(4));
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const checkOverflow = () => {
        if (scrollContainerRef.current) {
            const {scrollWidth, clientWidth, scrollLeft} = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollWidth > clientWidth + scrollLeft);
        }
    };

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const {scrollLeft, clientWidth} = scrollContainerRef.current;
            const newScrollLeft = direction === "left" ? scrollLeft - clientWidth / 2 : scrollLeft + clientWidth / 2;
            scrollContainerRef.current.scrollTo({left: newScrollLeft, behavior: "smooth"});
        }
    };

    const handleCategorySelect = (category) => {
        setSelectedNav(category);
        setAnchorEl(null);

        // If the selected category is in otherCategories, move it to preferredCategories
        if (otherCategories.includes(category)) {
            const slicedPreferredCategories = preferredCategories.filter(c => c !== category).slice(0, 3);
            const newPreferredCategories = [category, ...slicedPreferredCategories];
            const newOtherCategories = categories.filter(c => !newPreferredCategories.includes(c));
            setPreferredCategories(newPreferredCategories);
            setOtherCategories(newOtherCategories);
        }
    };

    useEffect(() => {
        checkOverflow();
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [categories]);

    if (isMobile) {
        return (
            <div className="scrollable-chips" style={{ width: '100%' }}>
                <button
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    className={`scrollable-chips__more-button ${selectedNav ? 'scrollable-chips__more-button--active' : 'scrollable-chips__more-button--inactive'}`}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '8px 16px'
                    }}
                >
                    <span style={{ fontWeight: 400 }}>{selectedNav || 'Select Category'}</span>
                    <KeyboardArrowDownIcon fontSize="small" />
                </button>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    className="scrollable-chips__menu"
                    PaperProps={{
                        className: "scrollable-chips__menu-paper",
                        style: { width: '100%' }
                    }}
                >
                    {categories.map((category, index) => (
                        <MenuItem
                            key={index}
                            onClick={() => handleCategorySelect(category)}
                            className={`scrollable-chips__menu-item ${selectedNav === category ? 'scrollable-chips__menu-item--active' : 'scrollable-chips__menu-item--inactive'}`}
                        >
                            {category}
                            {selectedNav === category && <i className="bi bi-check ms-2"></i>}
                        </MenuItem>
                    ))}
                </Menu>
            </div>
        );
    }

    return (
        <div className="scrollable-chips">
            {showLeftArrow && (
                <button
                    onClick={() => scroll("left")}
                    className="scrollable-chips__arrow-button"
                >
                    <ArrowBackIcon/>
                </button>
            )}

            <div
                ref={scrollContainerRef}
                onScroll={checkOverflow}
                className="scrollable-chips__container"
            >
                {preferredCategories.map((category, index) => {
                    const isActive = category === selectedNav;
                    return (
                        <button
                            key={index}
                            className={`scrollable-chips__chip ${isActive ? 'scrollable-chips__chip--active' : 'scrollable-chips__chip--inactive'}`}
                            onClick={() => setSelectedNav(category)}
                        >
                            {category}
                        </button>
                    );
                })}
                {!!otherCategories.length && (
                    <>
                        <button
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            className={`scrollable-chips__more-button scrollable-chips__more-button--inactive`}
                        >
                            More <i className="bi bi-chevron-down"></i>
                        </button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            className="scrollable-chips__menu"
                            PaperProps={{
                                className: "scrollable-chips__menu-paper"
                            }}
                        >
                            {otherCategories.map((category) => (
                                <MenuItem
                                    key={category}
                                    onClick={() => handleCategorySelect(category)}
                                    className={`scrollable-chips__menu-item ${selectedNav === category ? 'scrollable-chips__menu-item--active' : 'scrollable-chips__menu-item--inactive'}`}
                                >
                                    {category}
                                    {selectedNav === category && <i className="bi bi-check ms-2"></i>}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
            </div>

            {showRightArrow && (
                <button
                    onClick={() => scroll("right")}
                    className="scrollable-chips__arrow-button"
                >
                    <ArrowForwardIcon/>
                </button>
            )}
        </div>
    );
};

export default ScrollableChips;
